import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../services/api";

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching order:", error);
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN").format(price);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("vi-VN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gray-50 dark:bg-slate-950 py-6 sm:py-12 transition-colors duration-300"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-24 sm:h-24 bg-green-100 dark:bg-green-900/20 rounded-full mb-4 sm:mb-6 animate-bounce">
            <svg
              className="w-10 h-10 sm:w-16 sm:h-16 text-green-600 dark:text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Đặt hàng thành công! 🎉
          </h1>
          <p className="text-sm sm:text-xl text-gray-600 dark:text-gray-400">
            Cảm ơn bạn đã mua hàng tại TaHaShoes
          </p>
        </div>

        {order && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-5 sm:px-8 py-4 sm:py-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-blue-100 text-[10px] sm:text-sm mb-0.5 uppercase tracking-wider font-bold">Mã đơn hàng</p>
                  <p className="text-lg sm:text-2xl font-black">#{order._id.slice(-8)}</p>
                </div>
                <div className="text-right">
                  <p className="text-blue-100 text-[10px] sm:text-sm mb-0.5 uppercase tracking-wider font-bold">Trạng thái</p>
                  <span className="inline-block bg-yellow-400 text-yellow-900 px-3 sm:px-4 py-1 sm:py-2 rounded-full font-black text-xs sm:text-sm">
                    {order.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              <div className="mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-black text-gray-900 dark:text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                  <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">timeline</span>
                  Quy trình xử lý đơn hàng
                </h3>
                <div className="relative pl-6 sm:pl-8">
                  <div className="absolute left-2.5 sm:left-4 top-0 bottom-0 w-0.5 bg-gray-100 dark:bg-slate-800"></div>

                  {/* Steps with common logic */}
                  {[
                    { title: "Đơn hàng đã được đặt", date: order.createdAt, active: true, stepNum: 1 },
                    { 
                      title: "Đã xác nhận", 
                      date: order.confirmedAt, 
                      active: order.confirmedAt || ["Đã xác nhận", "Đang giao", "Đã giao"].some(s => order.status.includes(s)),
                      stepNum: 2,
                      defaultMsg: "Chúng tôi đang xác nhận đơn hàng"
                    },
                    { 
                      title: "Đang giao hàng", 
                      date: order.shippingAt, 
                      active: order.shippingAt || ["Đang giao", "Đã giao"].some(s => order.status.includes(s)),
                      stepNum: 3,
                      defaultMsg: "Đơn hàng đang trên đường đến bạn"
                    },
                    { 
                      title: "Đã giao thành công", 
                      date: order.deliveredAt, 
                      active: order.deliveredAt || ["Đã giao"].some(s => order.status.includes(s)),
                      stepNum: 4,
                      defaultMsg: "Đơn hàng đã được giao thành công"
                    }
                  ].map((step, idx) => (
                    <div key={idx} className={`relative flex items-start ${idx !== 3 ? "mb-6 sm:mb-8" : ""}`}>
                      <div className={`absolute -left-6 sm:-left-8 w-5 h-5 sm:w-8 sm:h-8 rounded-full flex items-center justify-center z-10 border-2 ${
                        step.active 
                        ? "bg-green-500 border-green-500 text-white shadow-lg shadow-green-100 dark:shadow-none" 
                        : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-400"
                      }`}>
                        {step.active ? (
                          <span className="material-symbols-outlined text-[14px] sm:text-[18px] font-black">check</span>
                        ) : (
                          <span className="text-[10px] sm:text-xs font-black">{step.stepNum}</span>
                        )}
                      </div>
                      <div className="ml-2 sm:ml-4">
                        <p className={`font-black text-sm sm:text-lg leading-tight mb-0.5 ${step.active ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-slate-700"}`}>
                          {step.title}
                        </p>
                        <p className={`text-[10px] sm:text-sm font-medium ${step.active ? "text-gray-500 dark:text-gray-400" : "text-gray-300 dark:text-slate-800"}`}>
                          {step.date ? formatDate(step.date) : (step.active ? "Vừa xong" : step.defaultMsg)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="bg-gray-50 dark:bg-slate-800/50 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-slate-800">
                  <h3 className="font-black text-gray-900 dark:text-white mb-3 flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider">
                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[20px]">person</span>
                    Thông tin nhận hàng
                  </h3>
                  <div className="space-y-1.5 text-xs sm:text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-bold text-gray-900 dark:text-white">Họ tên:</span> {order.customerName}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-bold text-gray-900 dark:text-white">Email:</span> {order.customerEmail || "---"}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-bold text-gray-900 dark:text-white">SĐT:</span> {order.customerPhone}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-slate-800/50 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-slate-800">
                  <h3 className="font-black text-gray-900 dark:text-white mb-3 flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider">
                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[20px]">location_on</span>
                    Địa chỉ giao hàng
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed">{order.address}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-slate-800 pt-5 sm:pt-6">
                <h3 className="font-black text-gray-900 dark:text-white mb-4 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                   <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">shopping_basket</span>
                   Chi tiết đơn hàng
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {order.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex gap-3 sm:gap-4 p-3 bg-gray-50 dark:bg-slate-800/30 rounded-2xl border border-gray-100 dark:border-slate-800"
                    >
                      <div className="w-14 h-14 sm:w-20 sm:h-20 relative flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://cdn-icons-png.flaticon.com/512/679/679821.png"; // Gift icon fallback
                          }}
                          className="w-full h-full object-cover rounded-xl border border-gray-200 dark:border-slate-700"
                        />
                        {(item.isGift || (item.name && item.name.toLowerCase().includes("quà tặng"))) && (
                          <div className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full p-1 shadow-sm">
                            <span className="material-symbols-outlined text-[12px]">redeem</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-0.5 text-xs sm:text-base truncate">
                          {item.name}
                        </h4>
                        <p className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-500 font-medium mb-1.5">
                          Màu: {item.selectedColor} | Size: {item.selectedSize}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 font-bold">
                            SL: x{item.qty}
                          </span>
                          <span className="font-black text-blue-600 dark:text-blue-400 text-xs sm:text-lg">
                            {formatPrice(item.price * item.qty)}₫
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-slate-800 mt-5 sm:mt-6 pt-5 sm:pt-6">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <span>Tạm tính</span>
                    <span className="font-black text-gray-900 dark:text-white">
                      {formatPrice(
                        order.subtotal ||
                          order.total -
                            (order.shippingFee || 0) +
                            (order.discountAmount || 0),
                      )}
                      ₫
                    </span>
                  </div>
                  {order.discountAmount > 0 && (
                    <div className="flex justify-between text-xs sm:text-sm text-green-600 dark:text-green-500">
                      <span className="flex items-center gap-1 font-bold">
                        <span className="material-symbols-outlined text-[16px]">local_offer</span>
                        Giảm giá ({order.voucherCode})
                      </span>
                      <span className="font-black">
                        -{formatPrice(order.discountAmount)}₫
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <span>Phí vận chuyển</span>
                    <span className={`font-black ${order.shippingFee === 0 ? "text-green-600" : "text-gray-900 dark:text-white"}`}>
                      {order.shippingFee === 0
                        ? "Miễn phí"
                        : `${formatPrice(order.shippingFee)}₫`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t border-gray-100 dark:border-slate-800 pt-3 sm:pt-4">
                    <span className="text-sm sm:text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">Tổng tiền</span>
                    <span className="text-xl sm:text-2xl font-black text-red-600 dark:text-red-500">
                      {formatPrice(order.total)}₫
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link
            to="/products"
            className="px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white rounded-xl font-black text-sm sm:text-base hover:bg-blue-700 transition-all text-center flex items-center justify-center gap-2 shadow-lg shadow-blue-100 dark:shadow-none"
          >
            <span className="material-symbols-outlined text-[20px]">shopping_cart</span>
            Tiếp tục mua sắm
          </Link>

          <Link
            to="/orders"
            className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-xl font-black text-sm sm:text-base hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-center flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[20px]">receipt_long</span>
            Đơn hàng của tôi
          </Link>
        </div>

        <div className="mt-8 sm:mt-12 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-2xl p-4 sm:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[24px] sm:text-[32px] flex-shrink-0">support_agent</span>
            <div>
              <h3 className="font-black text-gray-900 dark:text-white text-sm sm:text-base uppercase tracking-wider mb-1 sm:mb-2">Bạn cần hỗ trợ?</h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 leading-relaxed">
                Đội ngũ TaHaShoes luôn sẵn sàng giải đáp mọi thắc mắc về đơn hàng của bạn.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                <a
                  href="https://zalo.me/0765109784"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-900 dark:text-white hover:text-blue-600 transition-colors"
                >
                  <span className="p-1 px-2 bg-white dark:bg-slate-800 rounded font-black text-blue-600 dark:text-blue-400">HOT</span>
                  0765.109.784
                </a>
                <a 
                  href="https://zalo.me/0765109784" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                  Chat qua Zalo ngay
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
